import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, Check, Server, Key, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RADIUSConfig = () => {
  const { token } = useAuth();
  const [radiusInfo, setRadiusInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedItems, setCopiedItems] = useState({});

  useEffect(() => {
    if (token) {
      fetchRADIUSInfo();
    }
  }, [token]);

  const fetchRADIUSInfo = async () => {
    try {
      setLoading(true);
      
      // Get company settings for RADIUS config
      const response = await axios.get(`${API_URL}/api/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const settings = response.data.data;
        
        // Set RADIUS info
        setRadiusInfo({
          domain: window.location.hostname,
          publicIP: settings.radius_public_ip || '35.225.230.28',
          privateIP: settings.radius_private_ip || '10.64.133.96',
          authPort: 1812,
          acctPort: 1813,
          secret: settings.radius_secret || 'testing123',
          companyName: settings.company_name || 'ISP Company'
        });
      }
    } catch (error) {
      console.error('Error fetching RADIUS info:', error);
      toast.error('Gagal memuat konfigurasi RADIUS');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    // Modern clipboard API with fallback
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedItems(prev => ({ ...prev, [key]: true }));
        toast.success('Berhasil disalin!');
        setTimeout(() => {
          setCopiedItems(prev => ({ ...prev, [key]: false }));
        }, 2000);
      }).catch(() => {
        fallbackCopy(text, key);
      });
    } else {
      fallbackCopy(text, key);
    }
  };

  const fallbackCopy = (text, key) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopiedItems(prev => ({ ...prev, [key]: true }));
        toast.success('Berhasil disalin!');
        setTimeout(() => {
          setCopiedItems(prev => ({ ...prev, [key]: false }));
        }, 2000);
      } else {
        toast.error('Gagal menyalin');
      }
    } catch (err) {
      toast.error('Gagal menyalin');
    }
  };

  const generateMikrotikScript = () => {
    if (!radiusInfo) return '';
    
    return `# ========================================
# KONFIGURASI RADIUS - ${radiusInfo.companyName}
# ========================================

# 1. Tambah RADIUS Server (gunakan domain)
/radius add \\
  address=${radiusInfo.domain} \\
  secret=${radiusInfo.secret} \\
  service=hotspot,ppp,login \\
  authentication-port=${radiusInfo.authPort} \\
  accounting-port=${radiusInfo.acctPort} \\
  timeout=3s \\
  comment="ISP Billing RADIUS"

# 2. Enable RADIUS di Hotspot
/ip hotspot profile set default \\
  use-radius=yes \\
  radius-accounting=yes \\
  radius-interim-update=00:05:00

# 3. Enable RADIUS di PPPoE
/ppp aaa set use-radius=yes accounting=yes

# 4. Test koneksi RADIUS
/radius incoming-request \\
  service=ppp \\
  username=testuser \\
  password=testpass

# 5. Monitoring
/radius print detail
/radius monitor 0`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-300">Memuat konfigurasi...</p>
        </div>
      </div>
    );
  }

  if (!radiusInfo) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
          <p className="text-yellow-800">Konfigurasi RADIUS belum tersedia</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200">Konfigurasi RADIUS Server</h1>
          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
            Informasi koneksi RADIUS untuk MikroTik Router Anda
          </p>
        </div>
      </div>

      {/* Info Alert */}
      <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 dark:text-blue-400" />
            <div>
              <p className="text-sm text-blue-900 font-medium">Informasi Penting</p>
              <p className="text-xs text-blue-700 mt-1 dark:text-blue-300">
                Gunakan informasi di bawah ini untuk menghubungkan MikroTik Router Anda ke RADIUS server aplikasi ISP.
                Setelah terhubung, customer dapat login PPPoE/Hotspot menggunakan username dan password dari database aplikasi.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RADIUS Server Information */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Informasi RADIUS Server</h2>
          </div>

          <div className="space-y-4">
            {/* Domain/Hostname */}
            <div className="bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-slate-600 mb-1 dark:text-slate-300">Server Domain (Recommended)</p>
                  <p className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">{radiusInfo.domain}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(radiusInfo.domain, 'domain')}
                  className="ml-2"
                >
                  {copiedItems.domain ? (
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Public IP */}
            <div className="bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-slate-600 mb-1 dark:text-slate-300">Public IP (Alternative)</p>
                  <p className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">{radiusInfo.publicIP}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(radiusInfo.publicIP, 'publicIP')}
                  className="ml-2"
                >
                  {copiedItems.publicIP ? (
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Ports */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
                <p className="text-xs text-slate-600 mb-1 dark:text-slate-300">Authentication Port</p>
                <p className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">{radiusInfo.authPort}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
                <p className="text-xs text-slate-600 mb-1 dark:text-slate-300">Accounting Port</p>
                <p className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">{radiusInfo.acctPort}</p>
              </div>
            </div>

            {/* Shared Secret */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/30">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Key className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <p className="text-xs text-amber-900 font-medium">Shared Secret (Wajib Sama!)</p>
                  </div>
                  <p className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">{radiusInfo.secret}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(radiusInfo.secret, 'secret')}
                  className="ml-2"
                >
                  {copiedItems.secret ? (
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MikroTik Script */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Script MikroTik (Siap Pakai)</h2>
            <Button
              onClick={() => copyToClipboard(generateMikrotikScript(), 'script')}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {copiedItems.script ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Tersalin!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Script
                </>
              )}
            </Button>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-xs text-green-400 font-mono whitespace-pre">
              {generateMikrotikScript()}
            </pre>
          </div>

          <div className="mt-4 bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
            <p className="text-sm font-medium text-slate-800 mb-2 dark:text-slate-200">Cara Menggunakan:</p>
            <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside dark:text-slate-300">
              <li>Klik tombol "Copy Script" di atas</li>
              <li>Buka Winbox atau Terminal MikroTik</li>
              <li>Paste script ke Terminal MikroTik</li>
              <li>Tekan Enter untuk eksekusi</li>
              <li>Tunggu sampai selesai, test dengan perintah: <code className="bg-slate-200 px-1 rounded dark:bg-slate-700">/radius print</code></li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Tutorial Steps */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 dark:text-slate-200">Langkah-Langkah Konfigurasi</h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm dark:bg-indigo-900/40 dark:text-indigo-400">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800 mb-1 dark:text-slate-200">Copy Script di Atas</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Klik tombol "Copy Script" untuk menyalin seluruh konfigurasi RADIUS
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm dark:bg-indigo-900/40 dark:text-indigo-400">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800 mb-1 dark:text-slate-200">Login ke MikroTik</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Buka Winbox atau Terminal, login dengan admin credentials
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm dark:bg-indigo-900/40 dark:text-indigo-400">
                3
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800 mb-1 dark:text-slate-200">Paste & Execute</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Paste script ke Terminal MikroTik, tekan Enter untuk jalankan
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm dark:bg-indigo-900/40 dark:text-indigo-400">
                4
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800 mb-1 dark:text-slate-200">Verifikasi Koneksi</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Test koneksi dengan command: <code className="bg-slate-200 px-1 rounded text-xs dark:bg-slate-700">/radius print</code>
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold text-sm dark:bg-green-900/40 dark:text-green-400">
                ✓
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800 mb-1 dark:text-slate-200">Selesai!</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Customer sekarang bisa login PPPoE/Hotspot dengan username & password dari aplikasi
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 dark:text-slate-200">Troubleshooting</h2>
          
          <div className="space-y-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/30">
              <p className="text-sm font-medium text-red-900 mb-1">❌ Timeout / No Response</p>
              <p className="text-xs text-red-700 dark:text-red-300">
                Solusi: Cek firewall MikroTik allow UDP port 1812, 1813. Test ping ke server: 
                <code className="bg-red-100 px-1 ml-1 rounded dark:bg-red-900/40">/ping {radiusInfo.domain}</code>
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 dark:bg-amber-900/30">
              <p className="text-sm font-medium text-amber-900 mb-1">⚠️ Access Reject</p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Solusi: Pastikan Shared Secret sama di MikroTik dan aplikasi: <strong>{radiusInfo.secret}</strong>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
              <p className="text-sm font-medium text-blue-900 mb-1">ℹ️ User Online tapi No Bandwidth</p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Solusi: Sync customer ke RADIUS dari menu Pelanggan → Action → Sync to RADIUS
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RADIUSConfig;
